import React, { useState, useEffect, useRef,useMemo } from 'react';
import {Search, Hospital, Pill, ArrowLeft} from 'lucide-react';
import img from '../../utils/images/map-marker3.png';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {useNavigate} from "react-router-dom";

import {
  fetchAllHealthLocations,
  selectHospitals,
  selectPharmacies,
  selectIsAnyLoading,
} from '../../store/slices/Branches/Index';


interface Location {
  id: number;
  title: string;
  address: string;
  lat: number;
  lng: number;
  type: 'hospital' | 'pharmacy';
  single_phone_number: string;
}

declare global {
  interface Window {
    google: any;
    initMap?: () => void;
  }
}
const Maps_branches: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {  loading } = useAppSelector((state) => state.contact);

  const hospitals = useAppSelector(selectHospitals);
  const pharmacies = useAppSelector(selectPharmacies);
  const isLoadingData = useAppSelector(selectIsAnyLoading);
  const currentLanguage = useAppSelector((state) => state.language.currentLanguage);

  const currentLang = currentLanguage || localStorage.getItem('selectedLanguage') || 'az';
  const translations = {
    az: {
      title: "Klinikalar və Apteklər",
      subtitle: "Bu bölmədə əməkdaşlıq etdiyimiz klinika və apteklər ilə tanış ola bilərsiniz.",
      loading: "Məlumatlar yüklənir",
      locations: "Məkanlar",
      findClinics: "Klinika və aptekləri tapın",
      loadingData: "yüklənir...",
      search: "Axtar...",
      loadingText: "Yüklənir...",
      noResults: "Nəticə tapılmadı",
      tryOtherKeyword: "Başqa açar söz yoxlayın",
      hospital: "Xəstəxana",
      pharmacy: "Aptek",
      mapLoadError: "Xəritə yüklənə bilmədi",
      mapLoadErrorDetail: "Google Maps yüklənə bilmədi. Zəhmət olmasa API açarını yoxlayın.",
      mapApiNotAvailable: "Google Maps API mövcud deyil",
      mapCreateError: "Xəritə yaradıla bilmədi",
      reload: "Yenidən yüklə",
      mapLoading: "Xəritə yüklənir...",
      back: "Geri",
      call: "Zəng et",
      route: "Marşrut"
    },
    ru: {
      title: "Клиники и Аптеки",
      subtitle: "В этом разделе вы можете ознакомиться с клиниками и аптеками, с которыми мы сотрудничаем.",
      loading: "Загрузка данных",
      locations: "Места",
      findClinics: "Найдите клиники и аптеки",
      loadingData: "загрузка...",
      search: "Поиск...",
      loadingText: "Загрузка...",
      noResults: "Результаты не найдены",
      tryOtherKeyword: "Попробуйте другие ключевые слова",
      hospital: "Больница",
      pharmacy: "Аптека",
      mapLoadError: "Не удалось загрузить карту",
      mapLoadErrorDetail: "Не удалось загрузить Google Maps. Пожалуйста, проверьте API ключ.",
      mapApiNotAvailable: "Google Maps API недоступен",
      mapCreateError: "Не удалось создать карту",
      reload: "Перезагрузить",
      mapLoading: "Загрузка карты...",
      back: "Назад",
      call: "Позвонить",
      route: "Маршрут"
    },
    en: {
      title: "Clinics and Pharmacies",
      subtitle: "In this section you can get acquainted with the clinics and pharmacies we cooperate with.",
      loading: "Loading data",
      locations: "Locations",
      findClinics: "Find clinics and pharmacies",
      loadingData: "loading...",
      search: "Search...",
      loadingText: "Loading...",
      noResults: "No results found",
      tryOtherKeyword: "Try other keywords",
      hospital: "Hospital",
      pharmacy: "Pharmacy",
      mapLoadError: "Failed to load map",
      mapLoadErrorDetail: "Failed to load Google Maps. Please check the API key.",
      mapApiNotAvailable: "Google Maps API not available",
      mapCreateError: "Failed to create map",
      reload: "Reload",
      mapLoading: "Loading map...",
      back: "Back",
      call: "Call",
      route: "Route"
    }
  };

  const t = translations[currentLang as keyof typeof translations];

  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<Map<number, any>>(new Map());
  const infoWindowsRef = useRef<Map<number, any>>(new Map());
  const currentOpenInfoWindowRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string>('');
  const isFirstLoadRef = useRef(true);
  const scriptLoadedRef = useRef(false);

  const mapCenter = useMemo(() => ({ lat: 40.4093, lng: 49.8671 }), []);
  useEffect(() => {
    dispatch(fetchAllHealthLocations(currentLanguage));
  }, [dispatch, currentLanguage]);

  const locations: Location[] = [
    ...hospitals.map(hospital => ({
      id: hospital.id,
      title: hospital.name,
      address: hospital.address,
      lat: hospital.location.lat,
      lng: hospital.location.lng,
      type: 'hospital' as const,
      single_phone_number: hospital.phone_number
    })),
    ...pharmacies.map(pharmacy => ({
      id: pharmacy.id + 10000,
      title: pharmacy.name,
      address: pharmacy.address,
      lat: pharmacy.location.lat,
      lng: pharmacy.location.lng,
      type: 'pharmacy' as const,
      single_phone_number: pharmacy.phone_number
    }))
  ];
  const filteredLocations = locations.filter((location) =>
    location.title?.toLowerCase().includes(searchInput.toLowerCase()) ?? false
  );
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        setIsLoaded(true);
        return;
      }



      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        scriptLoadedRef.current = true;

        const checkLoaded = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkLoaded);
            setIsLoaded(true);
          }
        }, 100);

        return;
      }

      const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ;


      scriptLoadedRef.current = true;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&callback=initMap`;
      script.async = true;
      script.defer = true;

      // Add callback function
      window.initMap = () => {
        setIsLoaded(true);
        setLoadError('');
      };

      script.onerror = (error) => {
        console.error('Google Maps script loading error:', error);
        setLoadError(t.mapLoadErrorDetail);
        scriptLoadedRef.current = false;
      };

      document.head.appendChild(script);
    };

    loadGoogleMapsScript();

    return () => {
      if (window.initMap) {
        delete window.initMap;
      }
    };
  }, [t]);

  useEffect(() => {
    if (!isLoaded || !mapContainerRef.current || mapRef.current) {
      return;
    }

    try {
      const google = window.google;

      if (!google || !google.maps) {
        console.error('Google Maps API not available');
        setLoadError(t.mapApiNotAvailable);
        return;
      }

      const mapOptions = {
        center: new google.maps.LatLng(mapCenter.lat, mapCenter.lng),
        zoom: 11,
        mapTypeId: 'roadmap',
        gestureHandling: 'cooperative',
        clickableIcons: false,
        zoomControl: true,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        scrollwheel: false,
        styles: []
      };

      const map = new google.maps.Map(mapContainerRef.current, mapOptions);
      mapRef.current = map;

    } catch (error) {
      console.error('Map yaradıla bilmədi:', error);
      setLoadError(t.mapCreateError + ': ' + (error as Error).message);
    }
  }, [isLoaded, t,mapCenter.lat, mapCenter.lng]);

  // Add Markers
  useEffect(() => {
    if (!mapRef.current || !isLoaded || isLoadingData) {
      return;
    }



    const google = window.google;
    const map = mapRef.current;

    markersRef.current.forEach((marker) => {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    });
    markersRef.current.clear();

    infoWindowsRef.current.forEach((infoWindow) => {
      infoWindow.close();
    });
    infoWindowsRef.current.clear();

    if (currentOpenInfoWindowRef.current) {
      currentOpenInfoWindowRef.current.close();
      currentOpenInfoWindowRef.current = null;
    }

    const bounds = new google.maps.LatLngBounds();

    filteredLocations.forEach((medPoint) => {
      const position = new google.maps.LatLng(medPoint.lat, medPoint.lng);

      const marker = new google.maps.Marker({
        position: position,
        map: map,
        title: medPoint.title,
        icon: {
          url: img,
          scaledSize: new google.maps.Size(40, 50),
          anchor: new google.maps.Point(20, 50)
        },
        optimized: false,
        zIndex: 999,
        animation: google.maps.Animation.DROP
      });

      const contentString = `
        <div  class="mapinfo" style="padding:12px 8px; min-width: 100px;   white-space: nowrap; font-family: sans-serif; ">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #1f2937;">${medPoint.title}</h3>
<p style="margin: 0 0 12px 0; font-size: 14px; color: #6b7280; white-space: normal; word-wrap: break-word; overflow-wrap: break-word;">${medPoint.address}</p>          <div style="display: flex; gap: 8px;">
            <a href="tel:${medPoint.single_phone_number}" 
           style="flex: 1; padding: 8px 12px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; text-align: center; font-size: 14px; font-weight: 500; display: flex; align-items: center; justify-content: center; gap: 6px;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
          ${t.call}
        </a>
        <a href="https://www.google.com/maps/dir/?api=1&destination=${medPoint.lat},${medPoint.lng}" 
           target="_blank" 
           style="flex: 1; padding: 8px 12px; background: #10b981; color: white; text-decoration: none; border-radius: 6px; text-align: center; font-size: 14px; font-weight: 500; display: flex; align-items: center; justify-content: center; gap: 6px;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          ${t.route}
        </a>
          </div>
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({
        content: contentString,
        maxWidth: 360,
        disableAutoPan: false
      });

      marker.addListener('click', () => {
        if (currentOpenInfoWindowRef.current) {
          currentOpenInfoWindowRef.current.close();
        }

        map.setCenter(position);
        map.setZoom(16);

        setTimeout(() => {
          infoWindow.open({
            anchor: marker,
            map: map,
            shouldFocus: true
          });
          currentOpenInfoWindowRef.current = infoWindow;

        }, 300);
      });

      markersRef.current.set(medPoint.id, marker);
      infoWindowsRef.current.set(medPoint.id, infoWindow);
      bounds.extend(position);
    });

    if (filteredLocations.length > 0) {
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
      } else {
        map.fitBounds(bounds);

        google.maps.event.addListenerOnce(map, 'idle', () => {
          const currentZoom = map.getZoom();
          if (currentZoom && currentZoom > 14) {
            map.setZoom(14);
          }
        });
      }
    } else {
      map.setCenter(mapCenter);
      map.setZoom(11);
    }

  }, [filteredLocations, isLoaded, isLoadingData, t,mapCenter]);

  const handleLocationClick = (location: Location) => {
    setSelectedLocation(location);

    if (window.innerWidth < 1024 && mapSectionRef.current) {
      mapSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    setTimeout(() => {
      const marker = markersRef.current.get(location.id);
      const infoWindow = infoWindowsRef.current.get(location.id);

      if (marker && infoWindow && mapRef.current) {
        const map = mapRef.current;

        if (currentOpenInfoWindowRef.current) {
          currentOpenInfoWindowRef.current.close();
        }

        const position = marker.getPosition();

        map.panTo(position);
        map.setZoom(16);

        setTimeout(() => {
          infoWindow.open({
            anchor: marker,
            map: map,
            shouldFocus: true
          });
          currentOpenInfoWindowRef.current = infoWindow;
        }, 300);
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-semibold text-gray-900  md:pl-0">{t.title}</h1>
      <p className="text-stone-600 leading-relaxed text-base">
        {t.subtitle}
      </p>

      <main className="mx-auto py-8 md:py-8 px-2 md:px-0">
        <div className="flex flex-col lg:flex-row gap-4 md:gap-6">

          {/* Left Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0">
            {/* Header Card */}
            <div className="bg-white mb-4 rounded-lg overflow-hidden shadow-sm">
              <div className="bg-[#a80067] p-6">
                <h2 className="text-2xl font-bold text-white">{t.locations}</h2>
                <p className="text-white/80 text-sm mt-1">
                  {t.findClinics}
                  {isLoadingData && ` (${t.loadingData})`}
                </p>
              </div>

              {/* Search Input */}
              <div className="p-4 bg-gray-50">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t.search}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full px-4 py-3 pl-11 bg-white border-2 border-[#a80067]/20 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#a80067] focus:ring-2 focus:ring-[#a80067]/20 transition-all shadow-sm"
                    disabled={isLoadingData}
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a80067]" />
                </div>
              </div>
            </div>

            {/* Location List */}
            <div className="bg-white rounded-lg shadow-sm p-4 max-h-[500px] overflow-y-auto custom-scrollbar-pink">
              <div className="space-y-3">
                {isLoadingData ? (
                  <div className="text-center py-8">
                    <div className="inline-block p-4 bg-gray-100 rounded-full mb-3">
                      <div className="w-8 h-8 border-4 border-[#a80067] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-500 font-medium">{t.loadingText}</p>
                  </div>
                ) : filteredLocations.length > 0 ? (
                  filteredLocations.map((location) => (
                    <div
                      key={location.id}
                      onClick={() => handleLocationClick(location)}
                      className={`group relative overflow-hidden rounded-xl cursor-pointer transition-all duration-300 ${
                        selectedLocation?.id === location.id
                          ? 'bg-gradient-to-r from-[#a80067] to-[#c0007f] shadow-lg shadow-[#a80067]/30 scale-[1.02]'
                          : 'bg-gray-50 hover:bg-gray-100 hover:shadow-md'
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {location.type === 'hospital' ? (
                                <Hospital className={`w-5 h-5 ${selectedLocation?.id === location.id ? 'text-white' : 'text-[#a80067]'}`} />
                              ) : (
                                <Pill className={`w-5 h-5 ${selectedLocation?.id === location.id ? 'text-white' : 'text-[#a80067]'}`} />
                              )}
                              <p className={`font-bold text-base ${selectedLocation?.id === location.id ? 'text-white' : 'text-gray-800'}`}>
                                {location.title}
                              </p>
                            </div>
                            <p className={`text-sm leading-relaxed ${selectedLocation?.id === location.id ? 'text-white/90' : 'text-gray-600'}`}>
                              {location.address}
                            </p>
                            <div className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${
                              selectedLocation?.id === location.id
                                ? 'bg-white/20 text-white'
                                : 'bg-[#a80067]/10 text-[#a80067]'
                            }`}>
                              {location.type === 'hospital' ? t.hospital : t.pharmacy}
                            </div>
                          </div>
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            selectedLocation?.id === location.id
                              ? 'bg-white'
                              : 'bg-[#a80067] opacity-0 group-hover:opacity-100'
                          } transition-opacity`}></div>
                        </div>
                      </div>

                      <div className={`absolute top-0 right-0 w-16 h-16 transform translate-x-8 -translate-y-8 rounded-full ${
                        selectedLocation?.id === location.id
                          ? 'bg-white/10'
                          : 'bg-[#a80067]/5'
                      }`}></div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="inline-block p-4 bg-gray-100 rounded-full mb-3">
                      <Search className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">{t.noResults}</p>
                    <p className="text-gray-400 text-sm mt-1">{t.tryOtherKeyword}</p>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Map Container */}
          <div ref={mapSectionRef} className="flex-1 min-h-[500px] md:min-h-[700px] relative">
            <div
              ref={mapContainerRef}
              className="bg-white shadow-lg rounded-lg h-[500px] md:h-[700px] w-full"
            />

            {loadError && (
              <div className="absolute inset-0 flex items-center justify-center bg-red-50 rounded-lg p-4">
                <div className="text-center max-w-md">
                  <p className="text-red-600 font-semibold mb-2">{t.mapLoadError}</p>
                  <p className="text-red-500 text-sm mb-4">{loadError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    {t.reload}
                  </button>
                </div>
              </div>
            )}

            {!isLoaded && !loadError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a80067] mx-auto mb-4"></div>
                  <p className="text-gray-600">{t.mapLoading}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Back Button */}
      <div className="mt-8 px-2 md:px-0">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-[#a80067] hover:text-white hover:border-[#a80067] transition-all duration-200 shadow-sm hover:shadow font-medium"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">{t.back}</span>
        </button>
      </div>

      {/* Custom Styles */}
      <style>{`
        .custom-scrollbar-pink::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar-pink::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 10px;
        }
        .custom-scrollbar-pink::-webkit-scrollbar-thumb {
          background: #a80067;
          border-radius: 10px;
        }
        .custom-scrollbar-pink::-webkit-scrollbar-thumb:hover {
          background: #8a0054;
        }
        
        /* Google Maps InfoWindow styling */
        .gm-style-iw-c {
          border-radius: 8px !important;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
        }
        .gm-style-iw-d {
          overflow: visible !important;
        }
        .gm-style .gm-style-iw-tc::after {
          background: white !important;
        }
        
         @media (max-width: 600px) {
          .mapinfo {
            padding: 4px !important;
          }
          .gm-ui-hover-effect{
    
              left:12px;
          }
        }
     
      `}</style>
    </div>
  );
};

export default Maps_branches;